"use client";

import { auth } from "@/firebase/firebase";
import { onIdTokenChanged, signInAnonymously, User } from "firebase/auth";

let authReadyPromise: Promise<void> | null = null;
let anonymousSignInPromise: Promise<User | null> | null = null;
let tokenPromiseState:
  | {
      key: string;
      promise: Promise<string | null>;
    }
  | null = null;
let idTokenCookieSyncSubscribers = 0;
let stopIdTokenCookieSync: (() => void) | null = null;

const FIREBASE_ID_TOKEN_COOKIE_NAME = "firebaseIdToken";
const FIREBASE_ID_TOKEN_MAX_AGE_SECONDS = 60 * 60;

const isSecureCookieContext = () => {
  if (typeof window === "undefined") {
    return true;
  }

  return window.location.protocol === "https:";
};

const buildFirebaseIdTokenCookie = (token: string | null) => {
  const secureAttribute = isSecureCookieContext() ? "; secure" : "";
  const lifetimeAttribute = token
    ? `; max-age=${FIREBASE_ID_TOKEN_MAX_AGE_SECONDS}`
    : "; expires=Thu, 01 Jan 1970 00:00:00 GMT";

  return `${FIREBASE_ID_TOKEN_COOKIE_NAME}=${token ?? ""}; path=/${lifetimeAttribute}${secureAttribute}; samesite=lax`;
};

const getTokenPromiseKey = (
  forceRefresh: boolean,
  ensureAnonymous: boolean,
) => {
  const currentUser = auth.currentUser;
  return [
    currentUser?.uid ?? "no-user",
    currentUser ? (currentUser.isAnonymous ? "anonymous" : "authenticated") : "missing",
    ensureAnonymous ? "allow-anonymous" : "no-anonymous",
    forceRefresh ? "fresh" : "cached",
  ].join(":");
};

export const waitForFirebaseAuthReady = async (): Promise<void> => {
  if (typeof window === "undefined") {
    return;
  }

  if (typeof auth.authStateReady === "function") {
    await auth.authStateReady();
    return;
  }

  if (!authReadyPromise) {
    authReadyPromise = new Promise((resolve) => {
      const unsubscribe = auth.onAuthStateChanged(() => {
        unsubscribe();
        resolve();
      });
    });
  }

  await authReadyPromise;
};

export const ensureAnonymousFirebaseUser = async (): Promise<User | null> => {
  await waitForFirebaseAuthReady();

  if (auth.currentUser) {
    return auth.currentUser;
  }

  if (!anonymousSignInPromise) {
    anonymousSignInPromise = signInAnonymously(auth)
      .then((credential) => credential.user)
      .finally(() => {
        anonymousSignInPromise = null;
      });
  }

  return anonymousSignInPromise;
};

export const getFirebaseAuthToken = async ({
  forceRefresh = false,
  ensureAnonymous = true,
}: {
  forceRefresh?: boolean;
  ensureAnonymous?: boolean;
} = {}): Promise<string | null> => {
  const promiseKey = getTokenPromiseKey(forceRefresh, ensureAnonymous);

  if (tokenPromiseState?.key === promiseKey) {
    return tokenPromiseState.promise;
  }

  const promise = (async () => {
    await waitForFirebaseAuthReady();

    let currentUser = auth.currentUser;
    if (!currentUser && ensureAnonymous) {
      currentUser = await ensureAnonymousFirebaseUser();
    }

    if (!currentUser) {
      writeFirebaseIdTokenCookie(null);
      return null;
    }

    const idToken = await currentUser.getIdToken(forceRefresh);
    writeFirebaseIdTokenCookie(idToken);
    return idToken;
  })().finally(() => {
    if (tokenPromiseState?.promise === promise) {
      tokenPromiseState = null;
    }
  });

  tokenPromiseState = {
    key: promiseKey,
    promise,
  };

  return promise;
};

export const writeFirebaseIdTokenCookie = (token: string | null) => {
  if (typeof document === "undefined") {
    return;
  }

  document.cookie = buildFirebaseIdTokenCookie(token);
};

const syncFirebaseIdTokenCookie = async (user: User | null) => {
  if (!user) {
    writeFirebaseIdTokenCookie(null);
    return;
  }

  try {
    const idToken = await user.getIdToken();
    writeFirebaseIdTokenCookie(idToken);
  } catch (error) {
    console.warn(
      "firebaseSession: unable to sync Firebase ID token cookie:",
      (error as { code?: string })?.code ?? error,
    );
  }
};

export const startFirebaseIdTokenCookieSync = () => {
  if (typeof window === "undefined") {
    return () => {};
  }

  idTokenCookieSyncSubscribers += 1;

  if (!stopIdTokenCookieSync) {
    stopIdTokenCookieSync = onIdTokenChanged(auth, (user) => {
      void syncFirebaseIdTokenCookie(user);
    });

    void syncFirebaseIdTokenCookie(auth.currentUser);
  }

  return () => {
    idTokenCookieSyncSubscribers = Math.max(0, idTokenCookieSyncSubscribers - 1);

    if (idTokenCookieSyncSubscribers === 0 && stopIdTokenCookieSync) {
      stopIdTokenCookieSync();
      stopIdTokenCookieSync = null;
    }
  };
};
